import type { VariantProps } from "class-variance-authority";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariantsCva = cva(
	"group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-4xl border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pe-1.5 has-data-[icon=inline-start]:ps-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!",
	{
		variants: {
			variant: {
				default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
				secondary: "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
				destructive:
					"bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20",
				outline: "border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground",
				ghost: "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
				link: "text-primary underline-offset-4 hover:underline",
				/**
				 * ⚠️ **The SEVENTH variant, added because the sixth was the last one**
				 * (TRY-290's adversary pass). `manage/audit`'s `ACTION_VARIANT` maps every member of
				 * `AUDIT_ACTIONS` to a distinct variant one by one, and TRY-290's two invite
				 * actions took `ghost` and `link` — its own comment says a seventh action
				 * "genuinely needs a new variant in `ui/badge.tsx`", and this is that.
				 *
				 * Shaped after `destructive` rather than after `default`: a tinted background
				 * with matching text, so it reads as a status rather than as a filled chip,
				 * and so it stays legible in both themes. `--warning` is one of the two tokens
				 * (`globals.css:119`) with no shadcn equivalent, which is why this is a hand
				 * addition and not something the CLI emits.
				 */
				warning:
					"bg-warning/10 text-warning focus-visible:ring-warning/20 dark:bg-warning/20 dark:focus-visible:ring-warning/40 [a]:hover:bg-warning/20",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

/**
 * ⚠️ **Merges its own output, like `buttonVariants` (TRY-373).** The cva object is
 * module-local and this wrapper is what is exported, so a caller cannot get the
 * unmerged string. The conflict it settles is real: the base sets
 * `border border-transparent` while the `outline` variant sets `border-border`, and at
 * equal specificity the loser is decided by CSS source order rather than by which one
 * the caller asked for — which is TRY-271's defect exactly, over 42 call sites on
 * `buttonVariants`.
 *
 * It renders byte-identically today because every existing caller already wrapped it
 * in `cn`, and `cn(cn(raw))` collapses. What it removes is the NEXT caller's ability to
 * not do that.
 */
function badgeVariants(props?: Parameters<typeof badgeVariantsCva>[0]) {
	return cn(badgeVariantsCva(props));
}

function Badge({
	className,
	variant = "default",
	render,
	...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
	return useRender({
		defaultTagName: "span",
		props: mergeProps<"span">(
			{
				className: cn(badgeVariants({ variant }), className),
			},
			props,
		),
		render,
		state: {
			slot: "badge",
			variant,
		},
	});
}

export { Badge, badgeVariants };
